import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { BadgePercent, ArrowRight, Copy } from "lucide-react";

const SEEN_KEY = "flexline_exit_seen_session";
const OFFER_CODE = "SAVE10";

// Exit-intent discount popup — fires once per session when the user moves to leave.
export function ExitIntentPopup() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem(SEEN_KEY)) return;
        const onLeave = (e) => {
            if (e.clientY <= 0 && !sessionStorage.getItem(SEEN_KEY)) {
                sessionStorage.setItem(SEEN_KEY, "1");
                setOpen(true);
            }
        };
        // Fallback for mobile / no mouseleave: fire after 25s
        const t = setTimeout(() => {
            if (!sessionStorage.getItem(SEEN_KEY)) { sessionStorage.setItem(SEEN_KEY, "1"); setOpen(true); }
        }, 25000);
        document.addEventListener("mouseout", onLeave);
        return () => { document.removeEventListener("mouseout", onLeave); clearTimeout(t); };
    }, []);

    const claim = async () => {
        if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
        setBusy(true);
        try {
            await api.post("/promo/enter", { email, promo_key: "exit_intent_save10" });
        } catch { /* non-blocking lead capture */ }
        setBusy(false);
        navigator.clipboard?.writeText(OFFER_CODE).catch(() => {});
        localStorage.setItem("flexline_promo_code", OFFER_CODE);
        toast.success(`Code ${OFFER_CODE} saved — it'll auto-apply at checkout!`);
        setOpen(false);
        navigate("/mobile");
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-md overflow-hidden p-0" data-testid="exit-popup">
                <div className="bg-[#D97706] text-white px-6 pt-6 pb-5">
                    <div className="w-11 h-11 rounded-sm bg-white/15 grid place-items-center mb-3">
                        <BadgePercent className="w-6 h-6" />
                    </div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-white/80" data-testid="exit-eyebrow">Wait — before you go</p>
                    <DialogTitle className="font-display text-3xl font-light mt-1 text-white" data-testid="exit-title">
                        Save on your <span className="font-serif-italic">first line.</span>
                    </DialogTitle>
                    <DialogDescription className="sr-only">Code {OFFER_CODE} for savings on FlexLine Mobile wireless plans.</DialogDescription>
                </div>
                <div className="px-6 py-5">
                    <p className="text-sm text-zinc-600 leading-relaxed mb-4" data-testid="exit-body">
                        Join FlexLine Mobile today — unlimited talk &amp; text on a fast eSIM network, keep your number,
                        activate in minutes. Use code <span className="font-mono font-semibold text-[#4C1D95]">{OFFER_CODE}</span> for extra savings.
                    </p>
                    <div className="flex gap-2">
                        <Input type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="exit-email" />
                        <Button onClick={claim} disabled={busy} data-testid="exit-claim-btn" className="bg-[#4C1D95] text-white rounded-sm shrink-0">
                            {busy ? "…" : <>Claim offer <ArrowRight className="w-4 h-4 ml-1" /></>}
                        </Button>
                    </div>
                    <button onClick={() => { navigator.clipboard?.writeText(OFFER_CODE); localStorage.setItem("flexline_promo_code", OFFER_CODE); toast.success(`Code ${OFFER_CODE} saved`); }} data-testid="exit-copy-code" className="mt-3 w-full text-center text-xs text-zinc-500 hover:text-[#4C1D95] flex items-center justify-center gap-1">
                        <Copy className="w-3 h-3" /> Copy code {OFFER_CODE}
                    </button>
                    <button onClick={() => setOpen(false)} data-testid="exit-dismiss" className="mt-2 w-full text-center text-xs text-zinc-400 hover:text-zinc-600">No thanks</button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default ExitIntentPopup;
